import React, { useState, useEffect, useMemo } from 'react';
import { FeeService, StudentService, CourseService, SettingsService } from '../services/api';
import Modal from './common/Modal';
import { useSettings } from '../context/SettingsContext';
import Swal from 'sweetalert2';
import { getTerminology } from '../utils/terminology';

import { exportToExcel } from '../utils/exportHelper';

const DEFAULT_FEE_CATEGORIES = [
  { id: 'monthly-tuition', name: 'Monthly Tuition Fee', amount: 0, is_tuition: true }
];

const parseFeeCategories = (rawCategories) => {
  if (!rawCategories) return DEFAULT_FEE_CATEGORIES;

  try {
    const parsed = JSON.parse(rawCategories);
    return Array.isArray(parsed) ? parsed : DEFAULT_FEE_CATEGORIES;
  } catch (e) {
    return DEFAULT_FEE_CATEGORIES;
  }
};

const FeeList = () => {
  const { settings } = useSettings();
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const currentUserId = user?.id || user?.data?.id;
  const canExport = userRole === 'admin' || userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'; // Keep isAdmin tag for general admin-specific UI
  
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [courses, setCourses] = useState([]);
  const [filterCourse, setFilterCourse] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterMonth, setFilterMonth] = useState(String(new Date().getMonth() + 1));
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterBatches, setFilterBatches] = useState([]);

  const terminology = useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const studentTerm = terminology.student;
  const studentsTerm = terminology.students;
  const courseTerm = terminology.course;
  const coursesTerm = terminology.courses;
  const batchTerm = terminology.batch;
  const batchesTerm = terminology.batches;

  const [feeCategories, setFeeCategories] = useState(() => parseFeeCategories(settings?.fee_categories));
  
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', amount: 0, is_tuition: false });

  useEffect(() => {
    setFeeCategories(parseFeeCategories(settings?.fee_categories));
  }, [settings?.fee_categories]);

  const [formData, setFormData] = useState({
    student_id: '',
    title: 'Monthly Tuition Fee',
    amount: 0,
    due_date: new Date().toISOString().split('T')[0],
    status: 'pending',
    gen_type: 'single', // 'single' or 'family'
    father_cnic: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    selected_sibling_ids: [],
    late_fine: 0,
    selected_categories: []
  });

  // New State for Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [familySearchCnic, setFamilySearchCnic] = useState('');
  const [familyFees, setFamilyFees] = useState([]);
  const [selectedFamilyFeeIds, setSelectedFamilyFeeIds] = useState([]);
  const [paymentData, setPaymentData] = useState({
    payment_method: 'Cash',
    paid_date: new Date().toISOString().split('T')[0],
    payment_account: '',
    late_fine: 0
  });
  const [generatePeriod, setGeneratePeriod] = useState({
    type: 'monthly', // 'monthly' or 'bulk'
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    title: '',
    amount: '',
    late_fine: 0,
    due_date: new Date().toISOString().split('T')[0],
    apply_discount: false,
    filter_type: 'all', // 'all', 'course', 'batch', 'family', 'student'
    course_id: '',
    batch_id: '',
    family_cnic: '',
    student_id: '',
    batches: []
  });

  const [stats, setStats] = useState({ total_collected: 0, pending: 0, overdue: 0 });

  // Modal Student Search & Filter State
  const [studentSearch, setStudentSearch] = useState('');
  const [modalFilterCourse, setModalFilterCourse] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [feeExportFilters, setFeeExportFilters] = useState({
    status: 'All',
    startDate: '',
    endDate: '',
    includeAll: false
  });

  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerType, setLedgerType] = useState('single');
  const [ledgerYear, setLedgerYear] = useState(new Date().getFullYear());
  const [ledgerStudentId, setLedgerStudentId] = useState('');
  const [ledgerStudentSearch, setLedgerStudentSearch] = useState('');
  const [showLedgerStudentDropdown, setShowLedgerStudentDropdown] = useState(false);
  const [ledgerFamilyCnic, setLedgerFamilyCnic] = useState('');
  const [activeLedgerSiblingTab, setActiveLedgerSiblingTab] = useState('');

  const getYearlyFeesForStudent = (studentId, year) => {
    return fees.filter(f => {
      if (f.student_id !== parseInt(studentId)) return false;
      if (!f.due_date) return false;
      const [y] = f.due_date.split('-');
      return parseInt(y) === parseInt(year);
    });
  };

  const getMonthVouchers = (studentId, month, year) => {
    return fees.filter(f => {
      if (f.student_id !== parseInt(studentId)) return false;
      if (!f.due_date) return false;
      const [y, m] = f.due_date.split('-');
      return parseInt(m) === parseInt(month) && parseInt(y) === parseInt(year);
    });
  };

  const getYearlyStats = (studentId, year) => {
    const studentFees = getYearlyFeesForStudent(studentId, year);
    const totalBilled = studentFees.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const totalPaid = studentFees.filter(f => f.status === 'paid').reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const totalOutstanding = studentFees.filter(f => f.status !== 'paid').reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const totalVouchers = studentFees.length;
    const paidVouchers = studentFees.filter(f => f.status === 'paid').length;
    const collectionRate = totalBilled > 0 ? ((totalPaid / totalBilled) * 100).toFixed(0) : 0;

    return { totalBilled, totalPaid, totalOutstanding, totalVouchers, paidVouchers, collectionRate };
  };

  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'PKR': return 'Rs. ';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'INR': return '₹';
      case 'AED': return 'AED ';
      case 'USD': default: return '$';
    }
  };

  useEffect(() => {
    loadFees();
    loadStudents();
    loadCourses();
  }, []);

  const loadFees = async () => {
    try {
      const response = await FeeService.getAll();
      const data = response.data;
      setFees(data);
      calculateStats(data);
    } catch (error) {
      console.error("Error loading fees", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total_collected = data.filter(f => f.status === 'paid').reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const pending = data.filter(f => f.status === 'pending').reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    const overdue = data.filter(f => f.status === 'overdue').length;
    setStats({ total_collected, pending, overdue });
  };

  const loadStudents = async () => {
    try {
      const response = await StudentService.getAll();
      setStudents(response.data);
    } catch (error) {
      console.error("Error loading students", error);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const handleFilterCourseChange = (e) => {
    const courseId = e.target.value;
    setFilterCourse(courseId);
    setFilterBatch('');
    if (courseId) {
      const selected = courses.find(c => c.id === parseInt(courseId));
      setFilterBatches(selected?.batches || []);
    } else {
      setFilterBatches([]);
    }
  };

  const handleGenerateCourseChange = (e) => {
    const courseId = e.target.value;
    const selected = courses.find(c => c.id === parseInt(courseId));
    setGeneratePeriod({
        ...generatePeriod,
        course_id: courseId,
        batch_id: '',
        batches: selected?.batches || []
    });
  };



  const getSelectedStudent = (studentId) => {
    if (!studentId) return null;
    return students.find(s => s.id === parseInt(studentId));
  };

  const getStudentTuitionAmount = (student) => {
    if (!student) return 0;
    const baseAmount = parseFloat(student.course?.fee || 0);
    const discount = parseFloat(student.discount || 0);
    return Math.max(0, baseAmount - discount);
  };

  const getCategoryAmountForStudent = (category, student = null) => {
    if (category.is_tuition && student) {
      return getStudentTuitionAmount(student);
    }

    return parseFloat(category.custom_amount ?? category.amount ?? 0) || 0;
  };

  const calculateSelectedCategoriesTotal = (categories, student = null) => {
    return (categories || []).reduce((sum, category) => {
      return sum + getCategoryAmountForStudent(category, student);
    }, 0);
  };

  const getFamilyStudentsFromIds = (studentIds = []) => {
    return students.filter(s => studentIds.includes(s.id));
  };

  const calculateFormTotal = (data = formData) => {
    const selectedCategories = data.selected_categories || [];

    if (data.gen_type === 'family') {
      return getFamilyStudentsFromIds(data.selected_sibling_ids || []).reduce((sum, student) => {
        return sum + calculateSelectedCategoriesTotal(selectedCategories, student);
      }, 0);
    }

    return calculateSelectedCategoriesTotal(selectedCategories, getSelectedStudent(data.student_id));
  };

  const buildFeeTitle = (categories = formData.selected_categories, month = formData.month, year = formData.year) => {
    if (!categories || categories.length === 0) return '';

    const monthIndex = Math.max(0, (parseInt(month) || 1) - 1);
    const monthLabel = new Date(0, monthIndex).toLocaleString('en', { month: 'short' });
    return `${categories.map(c => c.name).join(' + ')} - ${monthLabel} ${year}`;
  };

  const syncTuitionCategoryAmounts = (categories, student) => {
    return (categories || []).map(category => {
      if (!category.is_tuition) return category;
      return { ...category, custom_amount: student ? getStudentTuitionAmount(student) : 0 };
    });
  };

  const handleSaveCategory = async () => {
    const normalizedCategory = {
      ...categoryForm,
      name: categoryForm.name.trim(),
      amount: parseFloat(categoryForm.amount) || 0,
      is_tuition: Boolean(categoryForm.is_tuition)
    };

    if (!normalizedCategory.name) return;

    let newCategories = [...feeCategories];
    if (editingCategory) {
      newCategories = newCategories.map(c => c.id === editingCategory.id ? { ...normalizedCategory, id: editingCategory.id } : c);
    } else {
      newCategories.push({ ...normalizedCategory, id: Date.now() });
    }
    setFeeCategories(newCategories);
    setFormData(prev => {
      const selectedCategories = (prev.selected_categories || []).map(category => {
        if (category.id !== editingCategory?.id) return category;
        return {
          ...category,
          ...normalizedCategory,
          id: editingCategory.id,
          custom_amount: normalizedCategory.is_tuition ? category.custom_amount : normalizedCategory.amount
        };
      });
      const nextFormData = {
        ...prev,
        selected_categories: selectedCategories,
        title: buildFeeTitle(selectedCategories, prev.month, prev.year)
      };

      return { ...nextFormData, amount: calculateFormTotal(nextFormData) };
    });
    
    try {
      const data = new FormData();
      data.append('fee_categories', JSON.stringify(newCategories));
      await SettingsService.updateSettings(data);
    } catch (err) {}
    
    setShowCategoryModal(false);
    setEditingCategory(null);
  };

  const handleDeleteCategory = async (id) => {
    const newCategories = feeCategories.filter(c => c.id !== id);
    setFeeCategories(newCategories);
    setFormData(prev => {
      const selectedCategories = (prev.selected_categories || []).filter(c => c.id !== id);
      const nextFormData = {
        ...prev,
        selected_categories: selectedCategories,
        title: buildFeeTitle(selectedCategories, prev.month, prev.year)
      };

      return { ...nextFormData, amount: calculateFormTotal(nextFormData) };
    });

    try {
      const data = new FormData();
      data.append('fee_categories', JSON.stringify(newCategories));
      await SettingsService.updateSettings(data);
    } catch (err) {}
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => {
      const selectedCategories = prev.selected_categories || [];
      const isSelected = selectedCategories.some(c => c.id === category.id);
      let newCategories = [...selectedCategories];
      
      if (isSelected) {
        newCategories = newCategories.filter(c => c.id !== category.id);
      } else {
        const selectedStudent = prev.gen_type === 'single' ? getSelectedStudent(prev.student_id) : null;
        const amount = category.is_tuition
          ? getCategoryAmountForStudent(category, selectedStudent)
          : parseFloat(category.amount || 0);

        newCategories.push({ ...category, custom_amount: amount });
      }

      const nextFormData = {
        ...prev,
        selected_categories: newCategories,
        title: buildFeeTitle(newCategories, prev.month, prev.year)
      };

      return { ...nextFormData, amount: calculateFormTotal(nextFormData) };
    });
  };

  const handleCategoryAmountChange = (categoryId, newAmount) => {
    setFormData(prev => {
      const newCategories = (prev.selected_categories || []).map(c => {
        if (c.id === categoryId) {
          return { ...c, custom_amount: newAmount };
        }
        return c;
      });
      const nextFormData = { ...prev, selected_categories: newCategories };

      return { ...nextFormData, amount: calculateFormTotal(nextFormData) };
    });
  };



  const handleInputChange = (e) => {


    const { name, value } = e.target;
    let newFormData = { ...formData, [name]: value };

    // Auto-update title if month or year changes
    if (name === 'month' || name === 'year') {
      const yearLabel = name === 'year' ? value : formData.year;
      const monthValue = name === 'month' ? value : formData.month;
      newFormData.title = buildFeeTitle(newFormData.selected_categories, monthValue, yearLabel);
      
      // Also update due date to 10th of that month
      newFormData.due_date = `${yearLabel}-${monthValue.toString().padStart(2, '0')}-10`;
    }

    if (name === 'father_cnic') {
      const familyStudents = students.filter(s => s.father_cnic === value);
      newFormData.selected_sibling_ids = familyStudents.map(s => s.id);
    }

    if (name === 'student_id' && value) {
      const student = getSelectedStudent(value);
      newFormData.selected_categories = syncTuitionCategoryAmounts(newFormData.selected_categories, student);
    }

    newFormData.amount = calculateFormTotal(newFormData);
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((formData.selected_categories || []).length === 0) {
      Swal.fire('Error', 'Please select at least one fee type.', 'error');
      return;
    }

    try {
      const voucherTitle = formData.title || buildFeeTitle(formData.selected_categories, formData.month, formData.year);

      if (formData.gen_type === 'family') {
        const familyStudents = students.filter(s => formData.selected_sibling_ids.includes(s.id));
        if (familyStudents.length === 0) {
          Swal.fire('Error', 'No students found with this Father CNIC', 'error');
          return;
        }

        let createdCount = 0;
        let skippedCount = 0;

        await Promise.all(familyStudents.map(async student => {
          // Local duplicate check
          const exists = fees.some(f => f.student_id === student.id && f.title === voucherTitle && f.due_date.split('T')[0] === formData.due_date);
          
          if (exists) {
            skippedCount++;
            return;
          }

          const amount = calculateSelectedCategoriesTotal(formData.selected_categories, student);

          await FeeService.create({
            student_id: student.id,
            title: voucherTitle,
            amount: amount,
            late_fine: formData.late_fine,
            due_date: formData.due_date,
            status: 'pending'
          });
          createdCount++;
        }));

        if (createdCount > 0) {
          Swal.fire('Success', `Generated ${createdCount} vouchers. ${skippedCount > 0 ? `(${skippedCount} skipped as they already existed)` : ''}`, 'success');
        } else {
          Swal.fire('Info', 'Voucher(s) for this title and date already exist for this family.', 'info');
        }
      } else {
        if (!formData.student_id) {
          Swal.fire('Error', `Please select a ${studentTerm.toLowerCase()}.`, 'error');
          return;
        }

        const selectedStudent = getSelectedStudent(formData.student_id);
        const amount = calculateSelectedCategoriesTotal(formData.selected_categories, selectedStudent);

        // Single student duplicate check
        const exists = fees.some(f => f.student_id === parseInt(formData.student_id) && f.title === voucherTitle && f.due_date.split('T')[0] === formData.due_date);
        
        if (exists) {
            Swal.fire('Warning', 'A voucher with this title and date already exists for this student.', 'warning');
            return;
        }

        await FeeService.create({
          student_id: formData.student_id,
          title: voucherTitle,
          amount: amount,
          late_fine: formData.late_fine,
          due_date: formData.due_date,
          status: 'pending'
        });
        Swal.fire('Success', 'Voucher generated successfully', 'success');
      }
      
      setShowModal(false);
      loadFees();
      
      // Reset Form
      setFormData({
        student_id: '',
        title: '',
        amount: 0,
        due_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        gen_type: 'single',
        father_cnic: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        selected_sibling_ids: [],
        late_fine: 0,
        selected_categories: []
      });
    } catch (error) {
      console.error("Error creating fee", error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to generate voucher', 'error');
    }
  };

  const handleGenerateFees = async (e) => {
    e.preventDefault();
    if (formData.selected_categories.length === 0) {
      Swal.fire('Error', 'Please select at least one fee category', 'error');
      return;
    }
    try {
      let response;
      const filterParams = {
        course_id: (generatePeriod.filter_type === 'course' || generatePeriod.filter_type === 'batch') ? generatePeriod.course_id : null,
        batch_id: generatePeriod.filter_type === 'batch' ? generatePeriod.batch_id : null,
        family_cnic: generatePeriod.filter_type === 'family' ? generatePeriod.family_cnic : null,
        student_id: generatePeriod.filter_type === 'student' ? generatePeriod.student_id : null,
      };

      if (generatePeriod.type === 'monthly') {
        response = await FeeService.generate({
          month: generatePeriod.month,
          year: generatePeriod.year,
          ...filterParams
        });
      } else {
        if (!generatePeriod.title || (!generatePeriod.amount && !(generatePeriod.selected_categories && generatePeriod.selected_categories.length > 0))) {
          Swal.fire('Error', 'Please fill in Title and Amount/Categories', 'error');
          return;
        }
        response = await FeeService.bulkGenerate({
          title: generatePeriod.title,
          amount: generatePeriod.amount,
          categories: generatePeriod.selected_categories || [],
          late_fine: generatePeriod.late_fine,
          due_date: generatePeriod.due_date,
          apply_discount: generatePeriod.apply_discount,
          ...filterParams
        });
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: response.data.message || 'Fees generated successfully'
      });
      setShowGenerateModal(false);
      loadFees();
    } catch (error) {
      console.error("Error generating fees", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to generate fees'
      });
    }
  };

  const openPaymentModal = (fee) => {
    setSelectedFee(fee);
    setPaymentData({
      payment_method: 'Cash',
      paid_date: new Date().toISOString().split('T')[0],
      payment_account: '',
      late_fine: fee.late_fine || 0
    });
    setShowPaymentModal(true);
  };

  const handleFamilySearch = () => {
    if (!familySearchCnic) return;
    const pendingFamily = fees.filter(f => f.student?.father_cnic === familySearchCnic && f.status === 'pending');
    setFamilyFees(pendingFamily);
    setSelectedFamilyFeeIds(pendingFamily.map(f => f.id));
  };

  const handleFamilyPaymentSubmit = async (e) => {
    e.preventDefault();
    const feesToPay = familyFees.filter(f => selectedFamilyFeeIds.includes(f.id));
    if (feesToPay.length === 0) {
      Swal.fire('Error', 'Please select at least one fee to pay', 'error');
      return;
    }
    try {
      await Promise.all(feesToPay.map(fee => FeeService.update(fee.id, {
        status: 'paid',
        payment_method: paymentData.payment_method,
        paid_date: paymentData.paid_date,
        payment_account: paymentData.payment_account
      })));
      setShowFamilyModal(false);
      loadFees();
      Swal.fire({
        icon: 'success',
        title: 'Family Payment Recorded',
        text: 'All family dues have been marked as paid.',
        timer: 1500
      });
    } catch (error) {
      console.error("Error updating family payments", error);
      Swal.fire('Error', 'Failed to record family payments', 'error');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await FeeService.update(selectedFee.id, {
        status: 'paid',
        payment_method: paymentData.payment_method,
        paid_date: paymentData.paid_date,
        payment_account: paymentData.payment_account,
        late_fine: parseFloat(paymentData.late_fine) || 0
      });
      setShowPaymentModal(false);
      loadFees();
      Swal.fire({
        icon: 'success',
        title: 'Payment Recorded',
        text: 'Fee has been marked as paid.',
        timer: 1500
      });
    } catch (error) {
      console.error("Error updating payment", error);
      Swal.fire('Error', 'Failed to record payment', 'error');
    }
  };

  const handleRevertToPending = async (fee) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to revert payment for ${fee.student?.first_name}? This will mark the fee as PENDING again.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Revert it!',
      cancelButtonText: 'No, keep it paid'
    });

    if (result.isConfirmed) {
      try {
        await FeeService.update(fee.id, {
          status: 'pending',
          paid_date: null,
          payment_method: null
        });
        loadFees();
        Swal.fire(
          'Reverted!',
          'The fee has been marked as pending.',
          'success'
        );
      } catch (error) {
        console.error("Error reverting fee", error);
        Swal.fire('Error', 'Failed to revert fee status', 'error');
      }
    }
  };

  const handleDeleteFee = async (fee) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to permanently delete the fee voucher for ${fee.student?.first_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await FeeService.destroy(fee.id);
        loadFees();
        Swal.fire('Deleted!', 'Fee voucher has been removed.', 'success');
      } catch (error) {
        console.error("Error deleting fee", error);
        Swal.fire('Error', 'Failed to delete fee voucher', 'error');
      }
    }
  };

  const handleViewInvoice = (fee) => {
    setSelectedFee(fee);
    setShowInvoiceModal(true);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const performExport = () => {
    let dataToExport = fees;

    if (!feeExportFilters.includeAll) {
      dataToExport = fees.filter(fee => {
        const matchesStatus = feeExportFilters.status === 'All' || 
          (fee.status || 'pending').toLowerCase() === feeExportFilters.status.toLowerCase();
        
        let matchesDate = true;
        if (feeExportFilters.startDate && feeExportFilters.endDate) {
          const feeDate = new Date(fee.due_date);
          const start = new Date(feeExportFilters.startDate);
          const end = new Date(feeExportFilters.endDate);
          matchesDate = feeDate >= start && (feeDate <= end || fee.due_date.startsWith(feeExportFilters.endDate));
        }

        return matchesStatus && matchesDate;
      });
    }

    if (dataToExport.length === 0) {
      Swal.fire('No Data', 'No records found for the selected filters.', 'info');
      return;
    }

    const exportData = dataToExport.map(fee => ({
      'Voucher ID': fee.id,
      'Registration No': fee.student?.reg_no || fee.student_id,
      'Student Name': `${fee.student?.first_name} ${fee.student?.last_name}`,
      'Father Name': fee.student?.father_name || '-',
      'Title': fee.title,
      'Amount': fee.amount,
      'Currency': settings.currency || 'PKR',
      'Status': (fee.status || 'pending').toUpperCase(),
      'Due Date': fee.due_date ? fee.due_date.split('T')[0] : '-',
      'Paid Date': fee.paid_date ? fee.paid_date.split('T')[0] : '-',
      'Payment Method': fee.payment_method || '-',
      'Received By': fee.collector ? fee.collector.name : (fee.received_by || '-'),
      'Course': fee.student?.course?.name || '-',
      'Batch': fee.student?.batch?.name || '-'
    }));

    exportToExcel(exportData, `Fee_Records_Export_${new Date().toISOString().split('T')[0]}`);
    setShowExportModal(false);
  };


  const filteredFees = useMemo(() => {
    return fees.filter(fee => {
      // Search Filter
      const searchTermLower = searchTerm.toLowerCase();
      const studentName = `${fee.student?.first_name} ${fee.student?.last_name}`.toLowerCase();
      const fatherCNIC = fee.student?.father_cnic || '';
      const cnic = fee.student?.cnic || '';
      const studentID = fee.student?.id?.toString() || '';
      const regNo = fee.student?.reg_no?.toString()?.toLowerCase() || '';
      const rollNo = fee.student?.roll_no?.toString()?.toLowerCase() || '';
      
      const matchesSearch = 
        studentName.includes(searchTermLower) || 
        fatherCNIC.includes(searchTermLower) || 
        cnic.includes(searchTermLower) || 
        studentID === searchTermLower || 
        rollNo.includes(searchTermLower) ||
        regNo.includes(searchTermLower);

      // Status Filter
      const matchesStatus = statusFilter.toLowerCase() === 'all' || fee.status.toLowerCase() === statusFilter.toLowerCase();

      // Course Filter
      const matchesCourse = !filterCourse || String(fee.student?.course_id) === String(filterCourse);

      // Batch Filter
      const matchesBatch = !filterBatch || String(fee.student?.batch_id) === String(filterBatch);

      // Month/Year Filter (Robust string matching)
      let matchesMonth = true;
      let matchesYear = true;

      if (fee.due_date) {
        const [y, m] = fee.due_date.split('-');
        if (filterMonth !== 'all') {
          matchesMonth = parseInt(m) === parseInt(filterMonth);
        }
        if (filterYear !== 'all') {
          matchesYear = parseInt(y) === parseInt(filterYear);
        }
      }

      return matchesSearch && matchesStatus && matchesCourse && matchesBatch && matchesMonth && matchesYear;
    });
  }, [fees, searchTerm, statusFilter, filterCourse, filterBatch, filterMonth, filterYear]);

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="fw-bold">Fee Management</h2>
          <p className="text-muted">Track payments, invoices and student dues</p>
        </div>
        <div className="d-flex gap-2">
          {isAdmin && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-outline-primary rounded-pill px-4 shadow-sm" onClick={() => setShowGenerateModal(true)}>
              <i className="bi bi-magic me-2"></i> Auto Generate
            </button>
          )}
          <button className="btn btn-info text-white d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => { setShowLedgerModal(true); setLedgerStudentId(''); setLedgerStudentSearch(''); setLedgerFamilyCnic(''); }}>
            <i className="bi bi-calendar3"></i> Fee Ledger
          </button>
          <button className="btn btn-warning d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => { setFamilySearchCnic(''); setFamilyFees([]); setSelectedFamilyFeeIds([]); setShowFamilyModal(true); }}>
            <i className="bi bi-people-fill"></i> Family Fee
          </button>
          {isAdmin && (
            <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => { setShowModal(true); setStudentSearch(''); setModalFilterCourse(''); }}>
              <i className="bi bi-plus-lg"></i> Collect Fee
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total Collected</p>
            <h3 className="mb-0">{getCurrencySymbol(settings?.currency)}{stats.total_collected.toLocaleString()}</h3>
          </div>
          <div className="stat-icon bg-green-light">
            <i className="bi bi-cash-stack"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Pending Amount</p>
            <h3 className="mb-0">{getCurrencySymbol(settings?.currency)}{stats.pending.toLocaleString()}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-clock-history"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Overdue Invoices</p>
            <h3 className="mb-0">{stats.overdue}</h3>
          </div>
          <div className="stat-icon bg-danger-light text-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="p-4 border-bottom bg-white rounded-top shadow-sm">
          {/* Row 1: Search & Status */}
          <div className="row g-3 align-items-center mb-4">
            <div className="col-lg-4">
              <div className="bg-light p-1 rounded-pill d-flex shadow-sm" style={{ width: 'fit-content' }}>
                {['All', 'Paid', 'Pending', 'Overdue'].map(filter => (
                  <button
                    key={filter}
                    className={`btn btn-sm rounded-pill px-4 py-2 border-0 ${statusFilter === filter ? 'btn-primary shadow-sm fw-bold text-white' : 'text-muted'}`}
                    onClick={() => setStatusFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-lg-8">
              <div className="position-relative shadow-sm rounded-pill overflow-hidden border">
                <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
                <input
                  type="text"
                  className="form-control border-0 ps-5 bg-white"
                  placeholder={`Search ${studentTerm.toLowerCase()} by name, id or cnic...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ height: '48px' }}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Category Filters */}
          <div className="row g-2 align-items-center">
            <div className="col-12">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="text-muted small fw-bold me-2"><i className="bi bi-filter me-1"></i> Filter By:</span>
                
                {/* Course Filter */}
                <div className="input-group input-group-sm w-auto shadow-sm rounded-pill overflow-hidden border">
                  <span className="input-group-text border-0 bg-light ps-3"><i className="bi bi-book text-muted"></i></span>
                  <select 
                    className="form-select border-0 bg-white" 
                    value={filterCourse} 
                    onChange={handleFilterCourseChange}
                    style={{ height: '40px', minWidth: '140px' }}
                  >
                    <option value="">All {coursesTerm}</option>
                    {courses.map(course => <option key={course.id} value={course.id}>{course.name}</option>)}
                  </select>
                </div>

                {/* Batch Filter */}
                <div className="input-group input-group-sm w-auto shadow-sm rounded-pill overflow-hidden border">
                  <span className="input-group-text border-0 bg-light ps-3"><i className="bi bi-layers text-muted"></i></span>
                  <select 
                    className="form-select border-0 bg-white" 
                    value={filterBatch} 
                    onChange={(e) => setFilterBatch(e.target.value)}
                    disabled={!filterCourse}
                    style={{ height: '40px', minWidth: '140px' }}
                  >
                    <option value="">All {batchesTerm}</option>
                    {filterBatches.map(batch => <option key={batch.id} value={batch.id}>{batch.name}</option>)}
                  </select>
                </div>

                {/* Month Filter */}
                <div className="input-group input-group-sm w-auto shadow-sm rounded-pill overflow-hidden border">
                  <span className="input-group-text border-0 bg-light ps-3"><i className="bi bi-calendar-event text-muted"></i></span>
                  <select 
                    className="form-select border-0 bg-white" 
                    value={filterMonth} 
                    onChange={(e) => setFilterMonth(e.target.value)}
                    style={{ height: '40px', minWidth: '130px' }}
                  >
                    <option value="all">All Months</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={String(i + 1)}>{new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>

                {/* Year Filter */}
                <div className="input-group input-group-sm w-auto shadow-sm rounded-pill overflow-hidden border">
                  <span className="input-group-text border-0 bg-light ps-3"><i className="bi bi-calendar-check text-muted"></i></span>
                  <select 
                    className="form-select border-0 bg-white" 
                    value={filterYear} 
                    onChange={(e) => setFilterYear(e.target.value)}
                    style={{ height: '40px', minWidth: '120px' }}
                  >
                    <option value="all">All Years</option>
                    {[2024, 2025, 2026, 2027].map(year => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>

                <button className="btn btn-sm btn-light rounded-pill px-3 border ms-auto shadow-sm" onClick={() => { setSearchTerm(''); setStatusFilter('All'); setFilterCourse(''); setFilterBatch(''); setFilterMonth(String(new Date().getMonth() + 1)); setFilterYear(String(new Date().getFullYear())); }}>
                  <i className="bi bi-arrow-counterclockwise me-1"></i> Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">{studentTerm}</th>
                <th>Title</th>
                <th>Amount</th>
                <th>Late Fine</th>
                <th>Due Date</th>
                <th>Status</th>
                {isAdmin && <th>Collector</th>}
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? "7" : "6"} className="text-center py-5 text-muted">Loading fee records...</td></tr>
              ) : filteredFees.length > 0 ? (
                filteredFees.map((fee) => (
                  <tr key={fee.id}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-sm bg-light text-dark d-flex align-items-center justify-content-center rounded-circle fw-bold" style={{ width: 35, height: 35 }}>
                          {fee.student?.first_name?.charAt(0)}
                        </div>
                        <div className="d-flex flex-column">
                          <div className="fw-semibold text-dark">{fee.student?.first_name} {fee.student?.last_name}</div>
                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>ID: {fee.student?.reg_no || `#${fee.student_id}`}</small>
                        </div>
                      </div>
                    </td>
                    <td><span className="text-muted small">{fee.title || 'Tuition Fee'}</span></td>
                    <td><span className="fw-bold text-dark">{getCurrencySymbol(settings?.currency)}{fee.amount}</span></td>
                    <td><span className="text-danger small">{fee.late_fine > 0 ? `+${getCurrencySymbol(settings?.currency)}${fee.late_fine}` : '-'}</span></td>
                    <td>{new Date(fee.due_date).toLocaleDateString()}</td>
                    <td>
                      <span className={`badge rounded-pill fw-normal px-3 py-2 ${fee.status === 'paid' ? 'bg-success-subtle text-success border-success' :
                        (fee.status === 'overdue' ? 'bg-danger-subtle text-danger border-danger' : 'bg-warning-subtle text-warning border-warning')
                        }`}>
                        {fee.status.toUpperCase()}
                      </span>
                    </td>
                    {isAdmin && (
                      <td>
                        <span className="small text-muted">{fee.collector ? fee.collector.name : '-'}</span>
                      </td>
                    )}
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        {fee.status !== 'paid' && (
                          <button
                            className="btn btn-icon btn-light rounded-circle text-success shadow-sm d-flex align-items-center justify-content-center p-0"
                            style={{ width: 35, height: 35 }}
                            title="Mark Paid"
                            onClick={() => openPaymentModal(fee)}
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                        )}
                        {fee.status === 'paid' && (isAdmin || fee.received_by === currentUserId) && (
                          <button
                            className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center p-0"
                            style={{ width: 35, height: 35 }}
                            title="Revert to Pending"
                            onClick={() => handleRevertToPending(fee)}
                          >
                            <i className="bi bi-arrow-counterclockwise"></i>
                          </button>
                        )}
                        <button
                          className="btn btn-icon btn-light rounded-circle text-primary shadow-sm d-flex align-items-center justify-content-center p-0"
                          style={{ width: 35, height: 35 }}
                          title="View Invoice"
                          onClick={() => handleViewInvoice(fee)}
                        >
                          <i className="bi bi-receipt"></i>
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center p-0"
                            style={{ width: 35, height: 35 }}
                            title="Delete Voucher"
                            onClick={() => handleDeleteFee(fee)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No fee records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title="Generate Fee Invoice">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <div className="btn-group w-100 p-1 bg-light rounded-pill">
              <button
                type="button"
                className={`btn btn-sm rounded-pill border-0 ${formData.gen_type === 'single' ? 'btn-primary shadow-sm' : 'text-muted'}`}
                onClick={() => {
                  const nextFormData = { ...formData, gen_type: 'single' };
                  setFormData({ ...nextFormData, amount: calculateFormTotal(nextFormData) });
                }}
              >
                Single Student
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill border-0 ${formData.gen_type === 'family' ? 'btn-primary shadow-sm' : 'text-muted'}`}
                onClick={() => {
                  const nextFormData = { ...formData, gen_type: 'family' };
                  setFormData({ ...nextFormData, amount: calculateFormTotal(nextFormData) });
                }}
              >
                Entire Family
              </button>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label small text-muted fw-bold">Select Month</label>
              <select className="form-select" name="month" value={formData.month} onChange={handleInputChange}>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted fw-bold">Select Year</label>
              <select className="form-select" name="year" value={formData.year} onChange={handleInputChange}>
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label small text-muted fw-bold mb-0">Fee Types</label>
              <button
                type="button"
                className="btn btn-sm btn-link text-primary p-0"
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', amount: 0, is_tuition: false });
                  setShowCategoryModal(true);
                }}
              >
                <i className="bi bi-gear-fill me-1"></i>Manage
              </button>
            </div>
            <div className="row g-2">
              {feeCategories.map(category => {
                const selectedCategories = formData.selected_categories || [];
                const isSelected = selectedCategories.some(c => c.id === category.id);
                const selectedCategory = selectedCategories.find(c => c.id === category.id);
                const selectedStudent = formData.gen_type === 'single' ? getSelectedStudent(formData.student_id) : null;
                const displayAmount = selectedCategory
                  ? getCategoryAmountForStudent(selectedCategory, selectedStudent)
                  : parseFloat(category.amount || 0);

                return (
                  <div className="col-md-6" key={category.id}>
                    <div
                      className={`border rounded-3 p-3 h-100 cursor-pointer ${isSelected ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
                      onClick={() => handleCategoryToggle(category)}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input mt-1"
                          checked={isSelected}
                          onChange={() => handleCategoryToggle(category)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="small fw-bold text-dark">{category.name}</span>
                            {category.is_tuition && <span className="badge bg-info-subtle text-info border border-info-subtle" style={{ fontSize: '10px' }}>Tuition</span>}
                          </div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>
                            {category.is_tuition
                              ? (formData.gen_type === 'family' ? 'Per student course fee' : 'Student course fee')
                              : `${getCurrencySymbol(settings?.currency)}${parseFloat(category.amount || 0).toLocaleString()}`}
                          </div>
                        </div>
                        {isSelected && (
                          category.is_tuition && formData.gen_type === 'family' ? (
                            <span className="badge bg-white text-primary border">Auto</span>
                          ) : category.is_tuition ? (
                            <div className="text-end small fw-bold text-primary">
                              {getCurrencySymbol(settings?.currency)}{displayAmount.toLocaleString()}
                            </div>
                          ) : (
                            <input
                              type="number"
                              className="form-control form-control-sm text-end"
                              value={selectedCategory?.custom_amount ?? selectedCategory?.amount ?? 0}
                              onChange={(e) => handleCategoryAmountChange(category.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: 95 }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {feeCategories.length === 0 && (
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-light border w-100 rounded-3 py-3 text-muted"
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({ name: '', amount: 0, is_tuition: false });
                      setShowCategoryModal(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-2"></i>Add Fee Type
                  </button>
                </div>
              )}
            </div>
          </div>

          {formData.gen_type === 'single' ? (
            <>
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label small text-muted fw-bold">Filter by {courseTerm}</label>
                  <select 
                    className="form-select mb-3" 
                    value={modalFilterCourse} 
                    onChange={(e) => {
                      setModalFilterCourse(e.target.value);
                      const nextFormData = {
                        ...formData,
                        student_id: '',
                        selected_categories: syncTuitionCategoryAmounts(formData.selected_categories, null)
                      };
                      setFormData({ ...nextFormData, amount: calculateFormTotal(nextFormData) });
                      setStudentSearch('');
                    }}
                  >
                    <option value="">All {coursesTerm}</option>
                    {courses.map(course => <option key={course.id} value={course.id}>{course.name}</option>)}
                  </select>
                </div>
                
                <div className="col-md-12 position-relative">
                  <label className="form-label small text-muted fw-bold">Search & Select Student</label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0" 
                      placeholder="Type name, ID or CNIC..."
                      value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); setShowStudentDropdown(true); }}
                      onFocus={() => setShowStudentDropdown(true)}
                    />
                  </div>
                  
                  {showStudentDropdown && (
                    <div className="position-absolute w-100 mt-1 shadow-lg bg-white border rounded-3 overflow-auto" style={{ zIndex: 1000, maxHeight: '200px', left: 0 }}>
                      <div className="list-group list-group-flush">
                        {students
                          .filter(s => {
                            const matchesCourse = !modalFilterCourse || String(s.course_id) === String(modalFilterCourse);
                            const searchLower = studentSearch.toLowerCase();
                            const matchesName = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchLower);
                            const matchesCnic = (s.cnic || '').includes(searchLower) || (s.father_cnic || '').includes(searchLower);
                            const matchesId = s.id.toString().includes(searchLower);
                            const matchesRegNo = (s.reg_no || '').toLowerCase().includes(searchLower);
                            return matchesCourse && (matchesName || matchesCnic || matchesId || matchesRegNo);
                          })
                          .map(student => (
                            <button 
                              key={student.id} 
                              type="button"
                              className="list-group-item list-group-item-action p-2 text-start" 
                              onClick={() => {
                                handleInputChange({ target: { name: 'student_id', value: student.id.toString() } });
                                setStudentSearch(`${student.first_name} ${student.last_name} (S/O ${student.father_name || 'N/A'})`);
                                setShowStudentDropdown(false);
                              }}
                            >
                              <div className="fw-bold small text-dark">
                                {student.first_name} {student.last_name} <span className="text-muted fw-normal">(S/O {student.father_name || 'N/A'})</span>
                              </div>
                              <div className="text-muted mb-0" style={{ fontSize: '11px' }}>
                                ID: {student.reg_no || `#${student.id}`} | CNIC: {student.father_cnic || 'N/A'} | {courses.find(c => c.id === student.course_id)?.name}
                              </div>
                            </button>
                          ))
                        }
                        {students.filter(s => {
                          const matchesCourse = !modalFilterCourse || String(s.course_id) === String(modalFilterCourse);
                          const searchLower = studentSearch.toLowerCase();
                          const matchesName = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchLower);
                          const matchesCnic = (s.cnic || '').includes(searchLower) || (s.father_cnic || '').includes(searchLower);
                          return matchesCourse && (matchesName || matchesCnic);
                        }).length === 0 && <div className="p-3 text-center text-muted small">No students found</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="mb-3">
              <label className="form-label small text-muted fw-bold">Father's CNIC</label>
              <input 
                type="text" 
                className="form-control mb-3" 
                name="father_cnic" 
                value={formData.father_cnic} 
                onChange={handleInputChange} 
                required={formData.gen_type === 'family'} 
                placeholder="XXXXX-XXXXXXX-X"
              />
              {formData.father_cnic && (
                <div className="bg-light p-3 rounded-3 border">
                  <p className="small fw-bold text-dark mb-2">Discovered Siblings:</p>
                  {students.filter(s => s.father_cnic === formData.father_cnic).length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {students.filter(s => s.father_cnic === formData.father_cnic).map(student => {
                        const isSelected = formData.selected_sibling_ids.includes(student.id);
                        return (
                          <div 
                            key={student.id} 
                            className={`badge border shadow-sm px-3 py-2 rounded-pill fw-normal cursor-pointer transition-all ${isSelected ? 'bg-primary text-white border-primary' : 'bg-white text-muted border-light border-2'}`}
                            onClick={() => {
                              const newSelected = isSelected
                                ? formData.selected_sibling_ids.filter(id => id !== student.id)
                                : [...formData.selected_sibling_ids, student.id];
                              const nextFormData = { ...formData, selected_sibling_ids: newSelected };
                              setFormData({ ...nextFormData, amount: calculateFormTotal(nextFormData) });
                            }}
                            style={{ transition: 'all 0.3s' }}
                          >
                            <i className={`bi ${isSelected ? 'bi-check-circle-fill' : 'bi-circle'} me-1`}></i> {student.first_name} {student.last_name}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="small text-danger mb-0 mt-1"><i className="bi bi-exclamation-circle me-1"></i> No students found with this CNIC</p>
                  )}
                  <div className="mt-3 text-muted" style={{ fontSize: '11px' }}>
                    <i className="bi bi-info-circle me-1"></i> Creating these vouchers will apply {studentsTerm} specific discounts automatically.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Selected Fee Summary</label>
            <div className="border rounded-3 bg-light overflow-hidden">
              {(formData.selected_categories || []).length > 0 ? (
                <>
                  {(formData.selected_categories || []).map(category => {
                    const selectedStudent = formData.gen_type === 'single' ? getSelectedStudent(formData.student_id) : null;
                    const familyStudents = formData.gen_type === 'family' ? getFamilyStudentsFromIds(formData.selected_sibling_ids || []) : [];
                    const lineAmount = formData.gen_type === 'family'
                      ? familyStudents.reduce((sum, student) => sum + getCategoryAmountForStudent(category, student), 0)
                      : getCategoryAmountForStudent(category, selectedStudent);

                    return (
                      <div key={category.id} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-white">
                        <div>
                          <div className="small fw-bold text-dark">{category.name}</div>
                          {formData.gen_type === 'family' && (
                            <div className="text-muted" style={{ fontSize: '11px' }}>{familyStudents.length} selected {studentsTerm.toLowerCase()}</div>
                          )}
                        </div>
                        <div className="fw-semibold text-dark">
                          {getCurrencySymbol(settings?.currency)}{lineAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    );
                  })}
                  <div className="d-flex justify-content-between align-items-center px-3 py-3 bg-primary-subtle">
                    <div>
                      <span className="fw-bold text-primary d-block">Final Total</span>
                      <span className="small text-primary opacity-75 fw-bold">
                        {(formData.selected_categories || []).map(category => {
                          const selectedStudent = formData.gen_type === 'single' ? getSelectedStudent(formData.student_id) : null;
                          const familyStudents = formData.gen_type === 'family' ? getFamilyStudentsFromIds(formData.selected_sibling_ids || []) : [];
                          return formData.gen_type === 'family'
                            ? familyStudents.reduce((sum, student) => sum + getCategoryAmountForStudent(category, student), 0)
                            : getCategoryAmountForStudent(category, selectedStudent);
                        }).map(a => a.toLocaleString(undefined, { maximumFractionDigits: 2 })).join(' + ')}
                      </span>
                    </div>
                    <span className="fs-5 fw-bold text-primary">
                      {getCurrencySymbol(settings?.currency)}{calculateFormTotal().toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </>
              ) : (
                <div className="px-3 py-3 text-muted small bg-white">No fee type selected</div>
              )}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Due Date</label>
            <input type="date" className="form-control" name="due_date" value={formData.due_date} onChange={handleInputChange} required />
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill">Generate Vouchers</button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
        }}
        title={editingCategory ? 'Edit Fee Type' : 'Manage Fee Types'}
      >
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Fee Type Name</label>
          <input
            type="text"
            className="form-control"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            placeholder="e.g. Admission Fee"
          />
        </div>
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Default Amount ({getCurrencySymbol(settings?.currency)})</label>
          <input
            type="number"
            className="form-control"
            value={categoryForm.amount}
            onChange={(e) => setCategoryForm({ ...categoryForm, amount: e.target.value })}
          />
        </div>
        <div className="form-check mb-4">
          <input
            type="checkbox"
            className="form-check-input"
            id="feeTypeIsTuition"
            checked={categoryForm.is_tuition}
            onChange={(e) => setCategoryForm({ ...categoryForm, is_tuition: e.target.checked })}
          />
          <label className="form-check-label small" htmlFor="feeTypeIsTuition">Use student's course fee and discount</label>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-4">
          {editingCategory && (
            <button
              type="button"
              className="btn btn-light rounded-pill px-4"
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: '', amount: 0, is_tuition: false });
              }}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary rounded-pill px-4"
            onClick={handleSaveCategory}
            disabled={!categoryForm.name.trim()}
          >
            Save Fee Type
          </button>
        </div>

        <div className="border-top pt-3">
          <label className="form-label small text-muted fw-bold">Existing Fee Types</label>
          <div className="d-flex flex-column gap-2">
            {feeCategories.map(category => (
              <div key={category.id} className="d-flex align-items-center justify-content-between border rounded-3 p-2 bg-light">
                <div>
                  <div className="small fw-bold text-dark">
                    {category.name}
                    {category.is_tuition && <span className="badge bg-info-subtle text-info ms-2">Tuition</span>}
                  </div>
                  <div className="text-muted" style={{ fontSize: '11px' }}>
                    {category.is_tuition ? 'Course-based' : `${getCurrencySymbol(settings?.currency)}${parseFloat(category.amount || 0).toLocaleString()}`}
                  </div>
                </div>
                <div className="d-flex gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle"
                    title="Edit"
                    onClick={() => {
                      setEditingCategory(category);
                      setCategoryForm({
                        name: category.name,
                        amount: category.amount || 0,
                        is_tuition: Boolean(category.is_tuition)
                      });
                    }}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle text-danger"
                    title="Delete"
                    onClick={() => handleDeleteCategory(category.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
            {feeCategories.length === 0 && <div className="text-muted small border rounded-3 p-3 bg-light">No fee types added yet.</div>}
          </div>
        </div>
      </Modal>

      <Modal show={showExportModal} onClose={() => setShowExportModal(false)} title="Export Fee Data to Excel">
        <div className="p-2">
          <div className="mb-4">
            <div className="form-check form-switch mb-3">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="exportAllData" 
                checked={feeExportFilters.includeAll}
                onChange={(e) => setFeeExportFilters({...feeExportFilters, includeAll: e.target.checked})}
              />
              <label className="form-check-label fw-bold" htmlFor="exportAllData">Export All Data (Skip Filters)</label>
            </div>
            
            {!feeExportFilters.includeAll && (
              <div className="row g-3 animate__animated animate__fadeIn">
                <div className="col-12">
                  <label className="form-label small fw-bold">Payment Status</label>
                  <select 
                    className="form-select"
                    value={feeExportFilters.status}
                    onChange={(e) => setFeeExportFilters({...feeExportFilters, status: e.target.value})}
                  >
                    <option value="All">All Statuses</option>
                    <option value="Paid">Paid Only</option>
                    <option value="Pending">Pending Only</option>
                    <option value="Overdue">Overdue Only</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Start Date</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={feeExportFilters.startDate}
                    onChange={(e) => setFeeExportFilters({...feeExportFilters, startDate: e.target.value})}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">End Date</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={feeExportFilters.endDate}
                    onChange={(e) => setFeeExportFilters({...feeExportFilters, endDate: e.target.value})}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowExportModal(false)}>Cancel</button>
            <button type="button" className="btn btn-success px-4 rounded-pill" onClick={performExport}>
              <i className="bi bi-file-earmark-excel me-2"></i> Export Now
            </button>
          </div>
        </div>
      </Modal>


      <Modal show={showGenerateModal} onClose={() => setShowGenerateModal(false)} title="Batch Billing / Auto Generate">
        <form onSubmit={handleGenerateFees}>
          <div className="mb-4">
            <div className="btn-group w-100 p-1 bg-light rounded-pill">
              <button
                type="button"
                className={`btn rounded-pill border-0 ${generatePeriod.type === 'monthly' ? 'btn-primary shadow-sm' : 'text-muted'}`}
                onClick={() => setGeneratePeriod({ ...generatePeriod, type: 'monthly' })}
              >
                Monthly Tuition
              </button>
              <button
                type="button"
                className={`btn rounded-pill border-0 ${generatePeriod.type === 'bulk' ? 'btn-primary shadow-sm' : 'text-muted'}`}
                onClick={() => setGeneratePeriod({ ...generatePeriod, type: 'bulk' })}
              >
                Custom / Annual Charges
              </button>
            </div>
          </div>

          <div className="mb-4 pt-3 border-top">
            <label className="form-label small fw-bold mb-2">Generate For:</label>
            <div className="d-flex gap-4 mb-2">
              <div className="form-check">
                <input className="form-check-input" type="radio" name="filterType" id="filterAll" checked={generatePeriod.filter_type === 'all'} onChange={() => setGeneratePeriod({...generatePeriod, filter_type: 'all'})} />
                <label className="form-check-label small cursor-pointer" htmlFor="filterAll">All Students</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="filterType" id="filterCourse" checked={generatePeriod.filter_type === 'course'} onChange={() => setGeneratePeriod({...generatePeriod, filter_type: 'course'})} />
                <label className="form-check-label small cursor-pointer" htmlFor="filterCourse">By {terminology.course}</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="filterType" id="filterBatch" checked={generatePeriod.filter_type === 'batch'} onChange={() => setGeneratePeriod({...generatePeriod, filter_type: 'batch'})} />
                <label className="form-check-label small cursor-pointer" htmlFor="filterBatch">By {terminology.batch}</label>
              </div>
            </div>
            <div className="d-flex gap-4 mb-3">
              <div className="form-check">
                <input className="form-check-input" type="radio" name="filterType" id="filterFamily" checked={generatePeriod.filter_type === 'family'} onChange={() => setGeneratePeriod({...generatePeriod, filter_type: 'family'})} />
                <label className="form-check-label small cursor-pointer" htmlFor="filterFamily">By Family</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" name="filterType" id="filterStudent" checked={generatePeriod.filter_type === 'student'} onChange={() => setGeneratePeriod({...generatePeriod, filter_type: 'student'})} />
                <label className="form-check-label small cursor-pointer" htmlFor="filterStudent">By Student</label>
              </div>
            </div>

            {(generatePeriod.filter_type === 'course' || generatePeriod.filter_type === 'batch') && (
              <div className="row g-2 mb-2 animate__animated animate__fadeIn">
                <div className={generatePeriod.filter_type === 'batch' ? 'col-md-6' : 'col-md-12'}>
                   <label className="form-label small text-muted fw-bold">{terminology.course}</label>
                   <select className="form-select form-select-sm" value={generatePeriod.course_id} onChange={handleGenerateCourseChange} required>
                     <option value="">Select {terminology.course}...</option>
                     {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                   </select>
                </div>
                {generatePeriod.filter_type === 'batch' && (
                   <div className="col-md-6">
                      <label className="form-label small text-muted fw-bold">{terminology.batch}</label>
                      <select className="form-select form-select-sm" value={generatePeriod.batch_id} onChange={(e) => setGeneratePeriod({...generatePeriod, batch_id: e.target.value})} required>
                        <option value="">Select {terminology.batch}...</option>
                        {generatePeriod.batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                   </div>
                )}
              </div>
            )}

            {generatePeriod.filter_type === 'family' && (
              <div className="row g-2 mb-2 animate__animated animate__fadeIn">
                <div className="col-md-12">
                   <label className="form-label small text-muted fw-bold">Father's CNIC (Family)</label>
                   <input type="text" className="form-control form-control-sm" value={generatePeriod.family_cnic} onChange={(e) => setGeneratePeriod({...generatePeriod, family_cnic: e.target.value})} placeholder="XXXXX-XXXXXXX-X" required />
                </div>
              </div>
            )}

            {generatePeriod.filter_type === 'student' && (
              <div className="row g-2 mb-2 animate__animated animate__fadeIn">
                <div className="col-md-12">
                   <label className="form-label small text-muted fw-bold">Student ID</label>
                   <input type="text" className="form-control form-control-sm" value={generatePeriod.student_id} onChange={(e) => setGeneratePeriod({...generatePeriod, student_id: e.target.value})} placeholder="Enter Student ID" required />
                </div>
              </div>
            )}
          </div>

          {generatePeriod.type === 'monthly' ? (
            <>
              <p className="text-muted small mb-3">Generate monthly tuition invoices for all active {studentsTerm.toLowerCase()} based on their {terminology.course.toLowerCase()} fee.</p>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Month</label>
                  <select
                    className="form-select"
                    value={generatePeriod.month}
                    onChange={(e) => setGeneratePeriod({ ...generatePeriod, month: e.target.value })}
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Year</label>
                  <select
                    className="form-select"
                    value={generatePeriod.year}
                    onChange={(e) => setGeneratePeriod({ ...generatePeriod, year: e.target.value })}
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(year => (
                      <option key={year} value={String(year)}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted small mb-3">Create a custom fee entry for all active {studentsTerm.toLowerCase()} at once.</p>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label small fw-bold mb-0">Select Fee Categories</label>
                  <button type="button" className="btn btn-sm btn-link text-primary p-0" onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', amount: 0, is_tuition: false }); setShowCategoryModal(true); }}>
                    <i className="bi bi-gear-fill me-1"></i>Manage
                  </button>
                </div>
                <div className="card shadow-none border bg-light p-3 mb-3">
                  <div className="row g-2">
                    {feeCategories.map(cat => {
                      const isSelected = (generatePeriod.selected_categories || []).some(c => c.id === cat.id);
                      const selectedCat = (generatePeriod.selected_categories || []).find(c => c.id === cat.id);
                      return (
                        <div className="col-md-6" key={cat.id}>
                          <div className={`border rounded p-2 d-flex align-items-center gap-2 cursor-pointer ${isSelected ? 'border-primary bg-white' : 'bg-white'}`} onClick={() => {
                            let newCategories = [...(generatePeriod.selected_categories || [])];
                            if (isSelected) {
                              newCategories = newCategories.filter(c => c.id !== cat.id);
                            } else {
                              newCategories.push({ ...cat, custom_amount: cat.amount || 0 });
                            }
                            const newAmount = newCategories.reduce((sum, c) => sum + parseFloat(c.custom_amount || c.amount || 0), 0);
                            const newTitle = buildFeeTitle(newCategories, generatePeriod.month, generatePeriod.year);
                            setGeneratePeriod({ ...generatePeriod, selected_categories: newCategories, amount: newAmount, title: newTitle });
                          }}>
                            <input type="checkbox" className="form-check-input mt-0" checked={isSelected} readOnly />
                            <div className="flex-grow-1 small fw-bold text-dark">{cat.name} {cat.is_tuition && <span className="badge bg-info-subtle text-info border border-info-subtle ms-1" style={{fontSize:'10px'}}>Tuition</span>}</div>
                            {isSelected && !cat.is_tuition && (
                              <input type="number" className="form-control form-control-sm text-end" style={{width:'80px'}} value={selectedCat?.custom_amount || 0} onClick={e => e.stopPropagation()} onChange={e => {
                                const newCategories = (generatePeriod.selected_categories || []).map(c => c.id === cat.id ? {...c, custom_amount: e.target.value} : c);
                                const newAmount = newCategories.reduce((sum, c) => sum + parseFloat(c.custom_amount || c.amount || 0), 0);
                                setGeneratePeriod({ ...generatePeriod, selected_categories: newCategories, amount: newAmount });
                              }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {feeCategories.length === 0 && <div className="col-12 text-muted small">No fee categories configured. Please add some.</div>}
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label small fw-bold">Combined Fee Title (Auto-generated)</label>
                <input type="text" className="form-control" value={generatePeriod.title} onChange={(e) => setGeneratePeriod({ ...generatePeriod, title: e.target.value })} />
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Final Total Preview</label>
                  <div className="form-control bg-light fw-bold text-success border-success-subtle">{getCurrencySymbol(settings?.currency)}{generatePeriod.amount || 0}</div>
                  <small className="text-muted" style={{fontSize:'10px'}}>Tuition fees will auto-adjust per student</small>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Late Fine ({getCurrencySymbol(settings?.currency)})</label>
                  <input
                    type="number"
                    className="form-control"
                    value={generatePeriod.late_fine}
                    onChange={(e) => setGeneratePeriod({ ...generatePeriod, late_fine: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={generatePeriod.due_date}
                    onChange={(e) => setGeneratePeriod({ ...generatePeriod, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="applyDiscount"
                  checked={generatePeriod.apply_discount}
                  onChange={(e) => setGeneratePeriod({ ...generatePeriod, apply_discount: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor="applyDiscount">Apply {studentTerm} specific discounts</label>
              </div>
            </>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowGenerateModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill">Generate Now</button>
          </div>
        </form>
      </Modal>

      <Modal show={showFamilyModal} onClose={() => setShowFamilyModal(false)} title="Family Fee Collection" size="lg">
        <div className="mb-4">
          <label className="form-label small text-muted fw-bold">Search by Father's CNIC</label>
          <div className="input-group">
            <input 
              type="text" 
              className="form-control" 
              placeholder="XXXXX-XXXXXXX-X" 
              value={familySearchCnic} 
              onChange={(e) => setFamilySearchCnic(e.target.value)} 
            />
            <button className="btn btn-primary" type="button" onClick={handleFamilySearch}>Search Dues</button>
          </div>
        </div>
        
        {familyFees.length > 0 && (
          <form onSubmit={handleFamilyPaymentSubmit}>
            <div className="table-responsive mb-3">
              <table className="table table-bordered align-middle">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input 
                        type="checkbox" 
                        className="form-check-input shadow-sm border-secondary"
                        checked={selectedFamilyFeeIds.length === familyFees.length && familyFees.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFamilyFeeIds(familyFees.map(f => f.id));
                          } else {
                            setSelectedFamilyFeeIds([]);
                          }
                        }}
                      />
                    </th>
                    <th>Student Name</th>
                    <th>Fee Title</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {familyFees.map(fee => (
                    <tr key={fee.id}>
                      <td>
                        <input 
                          type="checkbox" 
                          className="form-check-input shadow-sm border-secondary"
                          checked={selectedFamilyFeeIds.includes(fee.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFamilyFeeIds([...selectedFamilyFeeIds, fee.id]);
                            } else {
                              setSelectedFamilyFeeIds(selectedFamilyFeeIds.filter(id => id !== fee.id));
                            }
                          }}
                        />
                      </td>
                      <td>{fee.student?.first_name} {fee.student?.last_name}</td>
                      <td>{fee.title}</td>
                      <td>{getCurrencySymbol(settings?.currency)}{fee.amount}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan="3" className="text-end">Total Due:</th>
                    <th className="text-danger fw-bold fs-5">
                      {getCurrencySymbol(settings?.currency)}
                      {familyFees.filter(f => selectedFamilyFeeIds.includes(f.id)).reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">Payment Method</label>
                <select
                  className="form-select"
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Mobile Wallet">Mobile Wallet (JazzCash/EasyPaisa)</option>
                  <option value="Credit Balance">Credit Balance (Prepaid)</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">Payment Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={paymentData.paid_date}
                  onChange={(e) => setPaymentData({ ...paymentData, paid_date: e.target.value })}
                />
              </div>
            </div>

            {(paymentData.payment_method === 'Bank Transfer' || paymentData.payment_method === 'Mobile Wallet') && (
              <div className="mb-4">
                <label className="form-label small text-muted fw-bold">Select Account</label>
                <div className="d-flex flex-wrap gap-3">
                  {settings.payment_accounts ? JSON.parse(settings.payment_accounts).map((acc, index) => (
                    <div key={index} className={`p-3 border rounded-3 flex-grow-1 cursor-pointer transition-all ${paymentData.payment_account === acc.name ? 'border-primary bg-primary-subtle shadow-sm' : 'bg-light'}`} onClick={() => setPaymentData({ ...paymentData, payment_account: acc.name })}>
                      <div className="form-check m-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="familyAccountSelect"
                          id={`familyAcc-${index}`}
                          checked={paymentData.payment_account === acc.name}
                          onChange={() => setPaymentData({ ...paymentData, payment_account: acc.name })}
                        />
                        <label className="form-check-label w-100 cursor-pointer" htmlFor={`familyAcc-${index}`}>
                          <div className="fw-bold small">{acc.name}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>{acc.number} ({acc.type})</div>
                        </label>
                      </div>
                    </div>
                  )) : (
                    <div className="text-muted small p-2 w-100 border rounded bg-warning-subtle border-warning-subtle">
                      No payment accounts configured in settings.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowFamilyModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-success px-4 rounded-pill" disabled={selectedFamilyFeeIds.length === 0}>Pay Selected Amount</button>
            </div>
          </form>
        )}
        {familySearchCnic && familyFees.length === 0 && (
          <div className="text-center py-4 text-muted">
            Click search to find pending dues. If none appear, the family has no pending fees.
          </div>
        )}
      </Modal>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Record Payment">
        <form onSubmit={handlePaymentSubmit}>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Payment Method</label>
            <select
              className="form-select"
              value={paymentData.payment_method}
              onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
            >
              <option value="Cash">Cash</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cheque">Cheque</option>
              <option value="Mobile Wallet">Mobile Wallet (JazzCash/EasyPaisa)</option>
              <option value="Credit Balance">Credit Balance (Prepaid)</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Payment Date</label>
            <input
              type="date"
              className="form-control"
              value={paymentData.paid_date}
              onChange={(e) => setPaymentData({ ...paymentData, paid_date: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Late Fine ({getCurrencySymbol(settings?.currency)})</label>
            <input
              type="number"
              className="form-control"
              value={paymentData.late_fine}
              onChange={(e) => setPaymentData({ ...paymentData, late_fine: e.target.value })}
              placeholder="Enter fine if any"
            />
          </div>

          {(paymentData.payment_method === 'Bank Transfer' || paymentData.payment_method === 'Mobile Wallet') && (
            <div className="mb-4 pt-2">
              <label className="form-label small text-muted fw-bold">Select Receiving Account</label>
              <div className="d-flex flex-column gap-2">
                {settings.payment_accounts ? JSON.parse(settings.payment_accounts).map((acc, index) => (
                  <div key={index} className={`p-2 border rounded-3 cursor-pointer transition-all ${paymentData.payment_account === acc.name ? 'border-primary bg-primary-subtle' : 'bg-light'}`} onClick={() => setPaymentData({ ...paymentData, payment_account: acc.name })}>
                    <div className="form-check m-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="accountSelect"
                        id={`acc-${index}`}
                        checked={paymentData.payment_account === acc.name}
                        onChange={() => setPaymentData({ ...paymentData, payment_account: acc.name })}
                      />
                      <label className="form-check-label w-100 cursor-pointer" htmlFor={`acc-${index}`}>
                        <div className="fw-bold small">{acc.name}</div>
                        <div className="text-muted" style={{ fontSize: '11px' }}>{acc.number} ({acc.type})</div>
                      </label>
                    </div>
                  </div>
                )) : (
                  <div className="text-muted small p-2 border rounded bg-warning-subtle border-warning-subtle">
                    Please add payment accounts in Settings.
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowPaymentModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-success px-4 rounded-pill">Confirm Payment</button>
          </div>
        </form>
      </Modal>

      {/* Invoice Modal */}
      <Modal show={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} title="Invoice Details" size="lg">
        {selectedFee && (
          <div className="invoice-container p-3">
            <div className="d-flex justify-content-between mb-4 pb-4 border-bottom align-items-center">
              <div className="d-flex align-items-center gap-3">
                {settings?.app_logo ? (
                  <img 
                    src={settings.app_logo.startsWith('http') ? settings.app_logo : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${settings.app_logo}`} 
                    alt="Logo" 
                    style={{ width: '60px', height: '60px', objectFit: 'contain' }} 
                  />
                ) : (
                  <div className="bg-primary text-white rounded d-flex align-items-center justify-content-center shadow-sm" style={{ width: '60px', height: '60px' }}>
                    <i className="bi bi-mortarboard-fill fs-2"></i>
                  </div>
                )}
                <div>
                  <h4 className="fw-bold text-primary mb-1">{settings?.school_name || 'Institute Name'}</h4>
                  <p className="text-muted small mb-0">{settings?.school_address}</p>
                  <p className="text-muted small mb-0">Contact: {settings?.school_phone}</p>
                </div>
              </div>
              <div className="text-end">
                <h5 className="fw-bold mb-1 text-uppercase tracking-wider">Invoice</h5>
                <p className="mb-0 small text-muted font-monospace">No: #INV-{selectedFee.id}</p>
                <p className="small text-muted mb-0">Date: {new Date(selectedFee.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-6">
                <p className="small text-muted fw-bold mb-1">BILL TO:</p>
                <h6 className="fw-bold mb-0">{selectedFee.student?.first_name} {selectedFee.student?.last_name}</h6>
                <p className="small text-muted mb-0">Student ID: <span className="fw-bold text-dark">{selectedFee.student?.reg_no || selectedFee.student?.id}</span></p>
                <p className="small text-muted mb-0">Student CNIC: {selectedFee.student?.cnic || 'N/A'}</p>
                <p className="small text-muted mb-0">Father CNIC: {selectedFee.student?.father_cnic || 'N/A'}</p>
                <p className="small text-muted">Contact: {selectedFee.student?.phone}</p>
              </div>
              <div className="col-6 text-end">
                <p className="small text-muted fw-bold mb-1">DUE DATE:</p>
                <p className="fw-bold text-danger">{new Date(selectedFee.due_date).toLocaleDateString()}</p>
              </div>
            </div>

            <table className="table table-bordered mb-4">
              <thead className="bg-light">
                <tr>
                  <th>Description</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedFee.title || 'Tuition Fee'}</td>
                  <td className="text-end">
                    {getCurrencySymbol(settings?.currency)}
                    <span className="d-print-none">
                      {selectedFee.student?.discount > 0 ? (parseFloat(selectedFee.amount) + parseFloat(selectedFee.student.discount)) : selectedFee.amount}
                    </span>
                    <span className="d-none d-print-inline">
                      {selectedFee.amount}
                    </span>
                  </td>
                </tr>
                {selectedFee.student?.discount > 0 && (
                  <tr className="d-print-none">
                    <td>Flat Discount</td>
                    <td className="text-end text-danger">-{getCurrencySymbol(settings?.currency)}{selectedFee.student.discount}</td>
                  </tr>
                )}
                {selectedFee.late_fine > 0 && (
                  <tr>
                    <td>Late Fine</td>
                    <td className="text-end text-danger">+{getCurrencySymbol(settings?.currency)}{selectedFee.late_fine}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th className="text-end">Total Payable</th>
                  <th className="text-end text-primary">{getCurrencySymbol(settings?.currency)}{(parseFloat(selectedFee.amount) + (parseFloat(selectedFee.late_fine) || 0)).toFixed(2)}</th>
                </tr>
              </tfoot>
            </table>

            <div className="bg-light p-3 rounded mb-4">
              <p className="small text-muted mb-1 fw-bold">STATUS:</p>
              <h5 className={`fw-bold mb-0 ${selectedFee.status === 'paid' ? 'text-success' : 'text-warning'}`}>
                {selectedFee.status.toUpperCase()}
              </h5>
               {selectedFee.status === 'paid' && (
                <p className="small text-muted mt-2 mb-0">
                  Paid via <strong>{selectedFee.payment_method}</strong> {selectedFee.payment_account && `(${selectedFee.payment_account})`} on {new Date(selectedFee.paid_date).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="text-center mt-5 pt-3 border-top">
              <p className="small text-muted mb-2">This is a computer generated invoice and does not require a signature.</p>
              <button className="btn btn-primary d-print-none rounded-pill px-4" onClick={() => window.print()}>
                <i className="bi bi-printer me-2"></i> Print Invoice
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal show={showLedgerModal} onClose={() => setShowLedgerModal(false)} title="Yearly Fee Ledger" size="lg">
        <div>
          {/* Selector for Single vs Family */}
          <div className="mb-4">
            <div className="btn-group w-100 p-1 bg-light rounded-pill">
              <button
                type="button"
                className={`btn btn-sm rounded-pill border-0 ${ledgerType === 'single' ? 'btn-primary shadow-sm text-white fw-bold' : 'text-muted'}`}
                onClick={() => { setLedgerType('single'); setLedgerStudentId(''); setLedgerStudentSearch(''); }}
              >
                Single Student Ledger
              </button>
              <button
                type="button"
                className={`btn btn-sm rounded-pill border-0 ${ledgerType === 'family' ? 'btn-primary shadow-sm text-white fw-bold' : 'text-muted'}`}
                onClick={() => { setLedgerType('family'); setLedgerFamilyCnic(''); }}
              >
                Family Group Ledger
              </button>
            </div>
          </div>

          {/* Form / Inputs */}
          <div className="row g-3 align-items-end mb-4">
            <div className="col-md-3">
              <label className="form-label small text-muted fw-bold">Select Year</label>
              <select className="form-select" value={ledgerYear} onChange={(e) => setLedgerYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {ledgerType === 'single' ? (
              <div className="col-md-9 position-relative">
                <label className="form-label small text-muted fw-bold">Search & Select Student</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0"><i className="bi bi-search"></i></span>
                  <input 
                    type="text" 
                    className="form-control border-start-0" 
                    placeholder="Type name, ID or CNIC..."
                    value={ledgerStudentSearch}
                    onChange={(e) => { setLedgerStudentSearch(e.target.value); setShowLedgerStudentDropdown(true); }}
                    onFocus={() => setShowLedgerStudentDropdown(true)}
                  />
                </div>
                
                {showLedgerStudentDropdown && (
                  <div className="position-absolute w-100 mt-1 shadow-lg bg-white border rounded-3 overflow-auto" style={{ zIndex: 1050, maxHeight: '200px', left: 0 }}>
                    <div className="list-group list-group-flush">
                      {students
                        .filter(s => {
                          const searchLower = ledgerStudentSearch.toLowerCase();
                          const matchesName = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchLower);
                          const matchesCnic = (s.cnic || '').includes(searchLower) || (s.father_cnic || '').includes(searchLower);
                          const matchesId = s.id.toString().includes(searchLower);
                          const matchesRegNo = (s.reg_no || '').toLowerCase().includes(searchLower);
                          return matchesName || matchesCnic || matchesId || matchesRegNo;
                        })
                        .map(student => (
                          <button 
                            key={student.id} 
                            type="button"
                            className="list-group-item list-group-item-action p-2 text-start" 
                            onClick={() => {
                              setLedgerStudentId(student.id.toString());
                              setLedgerStudentSearch(`${student.first_name} ${student.last_name}`);
                              setShowLedgerStudentDropdown(false);
                            }}
                          >
                            <div className="fw-bold small text-dark">{student.first_name} {student.last_name}</div>
                            <div className="text-muted mb-0" style={{ fontSize: '11px' }}>
                              ID: {student.reg_no || `#${student.id}`} | Father: {student.father_cnic || 'N/A'} | {courses.find(c => c.id === student.course_id)?.name}
                            </div>
                          </button>
                        ))
                      }
                      {students.filter(s => {
                        const searchLower = ledgerStudentSearch.toLowerCase();
                        const matchesName = `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchLower);
                        const matchesCnic = (s.cnic || '').includes(searchLower) || (s.father_cnic || '').includes(searchLower);
                        return matchesName || matchesCnic;
                      }).length === 0 && <div className="p-3 text-center text-muted small">No students found</div>}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="col-md-9">
                <label className="form-label small text-muted fw-bold">Enter Father's CNIC</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="XXXXX-XXXXXXX-X"
                  value={ledgerFamilyCnic}
                  onChange={(e) => {
                    setLedgerFamilyCnic(e.target.value);
                    const siblings = students.filter(s => s.father_cnic === e.target.value);
                    if (siblings.length > 0) {
                      setActiveLedgerSiblingTab(siblings[0].id.toString());
                    } else {
                      setActiveLedgerSiblingTab('');
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Results section */}
          {ledgerType === 'single' && ledgerStudentId && (
            <div className="ledger-results animate__animated animate__fadeIn">
              {/* Individual Stats cards */}
              {(() => {
                const stats = getYearlyStats(ledgerStudentId, ledgerYear);
                return (
                  <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                      <div className="p-3 border rounded bg-light text-center">
                        <div className="small text-muted fw-bold mb-1">Total Billed</div>
                        <h4 className="mb-0 text-dark fw-bold">{getCurrencySymbol(settings?.currency)}{stats.totalBilled}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="p-3 border rounded bg-success-subtle text-success text-center border-success-subtle">
                        <div className="small text-muted fw-bold mb-1">Total Paid</div>
                        <h4 className="mb-0 fw-bold">{getCurrencySymbol(settings?.currency)}{stats.totalPaid}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="p-3 border rounded bg-danger-subtle text-danger text-center border-danger-subtle">
                        <div className="small text-muted fw-bold mb-1">Outstanding</div>
                        <h4 className="mb-0 fw-bold">{getCurrencySymbol(settings?.currency)}{stats.totalOutstanding}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="p-3 border rounded bg-primary-subtle text-primary text-center border-primary-subtle">
                        <div className="small text-muted fw-bold mb-1">Payment Ratio</div>
                        <h4 className="mb-0 fw-bold">{stats.collectionRate}%</h4>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Monthly Ledger Table */}
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '15%' }}>Month</th>
                      <th>Voucher Details</th>
                      <th style={{ width: '15%' }}>Amount</th>
                      <th style={{ width: '15%' }}>Status</th>
                      <th style={{ width: '20%' }}>Payment Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(12)].map((_, i) => {
                      const monthVal = i + 1;
                      const monthName = new Date(0, i).toLocaleString('en', { month: 'long' });
                      const monthVouchers = getMonthVouchers(ledgerStudentId, monthVal, ledgerYear);
                      
                      if (monthVouchers.length === 0) {
                        return (
                          <tr key={monthVal} className="text-muted opacity-75">
                            <td className="fw-semibold">{monthName}</td>
                            <td colSpan="4" className="text-center py-2 small bg-light-subtle">No vouchers generated for this month</td>
                          </tr>
                        );
                      }

                      return monthVouchers.map((fee, idx) => (
                        <tr key={`${monthVal}-${idx}`}>
                          {idx === 0 && (
                            <td rowSpan={monthVouchers.length} className="fw-bold align-middle bg-white text-dark">{monthName}</td>
                          )}
                          <td>
                            <div className="fw-semibold text-dark small">{fee.title}</div>
                            <small className="text-muted" style={{ fontSize: '10px' }}>ID: #INV-{fee.id} | Due: {new Date(fee.due_date).toLocaleDateString()}</small>
                          </td>
                          <td>
                            <span className="fw-bold text-dark">{getCurrencySymbol(settings?.currency)}{fee.amount}</span>
                            {fee.late_fine > 0 && <div className="text-danger small" style={{ fontSize: '9px' }}>+ fine: {getCurrencySymbol(settings?.currency)}{fee.late_fine}</div>}
                          </td>
                          <td>
                            <span className={`badge rounded-pill fw-normal px-2 py-1 ${fee.status === 'paid' ? 'bg-success-subtle text-success border-success' : (fee.status === 'overdue' ? 'bg-danger-subtle text-danger border-danger' : 'bg-warning-subtle text-warning border-warning')}`}>
                              {fee.status.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            {fee.status === 'paid' ? (
                              <div className="small text-secondary">
                                <div>Method: <strong>{fee.payment_method || 'Cash'}</strong></div>
                                <div style={{ fontSize: '10px' }}>Date: {new Date(fee.paid_date).toLocaleDateString()}</div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ledgerType === 'family' && ledgerFamilyCnic && (
            <div className="ledger-results animate__animated animate__fadeIn">
              {(() => {
                const siblings = students.filter(s => s.father_cnic === ledgerFamilyCnic);
                if (siblings.length === 0) {
                  return (
                    <div className="alert alert-warning text-center rounded-3 border-0 bg-warning-subtle text-warning py-4 mb-0">
                      <i className="bi bi-exclamation-circle-fill fs-3 d-block mb-2"></i>
                      No students discovered with the Father CNIC: <strong>{ledgerFamilyCnic}</strong>
                    </div>
                  );
                }

                // Family Statistics
                let familyBilled = 0;
                let familyPaid = 0;
                let familyOutstanding = 0;
                
                siblings.forEach(s => {
                  const sStats = getYearlyStats(s.id, ledgerYear);
                  familyBilled += sStats.totalBilled;
                  familyPaid += sStats.totalPaid;
                  familyOutstanding += sStats.totalOutstanding;
                });
                
                const familyRate = familyBilled > 0 ? ((familyPaid / familyBilled) * 100).toFixed(0) : 0;

                return (
                  <div>
                    {/* Family Combined Stats Card */}
                    <div className="row g-3 mb-4">
                      <div className="col-12">
                        <div className="p-3 border rounded bg-white shadow-xs">
                          <h6 className="fw-bold mb-3 text-secondary d-flex align-items-center gap-2">
                            <i className="bi bi-people-fill text-primary"></i> Family Group Summary ({siblings.length} Siblings)
                          </h6>
                          <div className="row g-3 text-center">
                            <div className="col-6 col-md-3">
                              <div className="small text-muted mb-1">Total Billed</div>
                              <h5 className="mb-0 text-dark fw-bold">{getCurrencySymbol(settings?.currency)}{familyBilled}</h5>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="small text-muted mb-1 text-success">Total Paid</div>
                              <h5 className="mb-0 text-success fw-bold">{getCurrencySymbol(settings?.currency)}{familyPaid}</h5>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="small text-muted mb-1 text-danger">Outstanding Dues</div>
                              <h5 className="mb-0 text-danger fw-bold">{getCurrencySymbol(settings?.currency)}{familyOutstanding}</h5>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="small text-muted mb-1 text-primary">Family Payment Ratio</div>
                              <h5 className="mb-0 text-primary fw-bold">{familyRate}%</h5>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sibling Tab Selectors */}
                    <div className="nav nav-tabs border-bottom mb-4 gap-2">
                      {siblings.map(sibling => (
                        <button
                          key={sibling.id}
                          type="button"
                          className={`nav-link border-0 border-bottom border-3 py-2 px-3 fw-bold rounded-0 ${activeLedgerSiblingTab === sibling.id.toString() ? 'active text-primary border-primary' : 'text-muted border-transparent'}`}
                          onClick={() => setActiveLedgerSiblingTab(sibling.id.toString())}
                          style={{ transition: 'all 0.3s' }}
                        >
                          {sibling.first_name} {sibling.last_name}
                        </button>
                      ))}
                    </div>

                    {/* Selected Sibling Yearly Record */}
                    {activeLedgerSiblingTab && (
                      <div className="animate__animated animate__fadeIn">
                        {(() => {
                          const activeSiblingStats = getYearlyStats(activeLedgerSiblingTab, ledgerYear);
                          return (
                            <div className="row g-3 mb-4">
                              <div className="col-6 col-md-3">
                                <div className="p-2 border rounded bg-light text-center small">
                                  <div className="text-muted mb-1">Billed</div>
                                  <h6 className="mb-0 text-dark fw-bold">{getCurrencySymbol(settings?.currency)}{activeSiblingStats.totalBilled}</h6>
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="p-2 border rounded bg-success-subtle text-success text-center border-success-subtle small">
                                  <div className="text-muted mb-1">Paid</div>
                                  <h6 className="mb-0 fw-bold">{getCurrencySymbol(settings?.currency)}{activeSiblingStats.totalPaid}</h6>
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="p-2 border rounded bg-danger-subtle text-danger text-center border-danger-subtle small">
                                  <div className="text-muted mb-1">Outstanding</div>
                                  <h6 className="mb-0 fw-bold">{getCurrencySymbol(settings?.currency)}{activeSiblingStats.totalOutstanding}</h6>
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="p-2 border rounded bg-primary-subtle text-primary text-center border-primary-subtle small">
                                  <div className="text-muted mb-1">Ratio</div>
                                  <h6 className="mb-0 fw-bold">{activeSiblingStats.collectionRate}%</h6>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Selected Sibling Monthly Table */}
                        <div className="table-responsive">
                          <table className="table table-bordered table-hover align-middle">
                            <thead className="bg-light">
                              <tr>
                                <th style={{ width: '15%' }}>Month</th>
                                <th>Voucher Details</th>
                                <th style={{ width: '15%' }}>Amount</th>
                                <th style={{ width: '15%' }}>Status</th>
                                <th style={{ width: '20%' }}>Payment Info</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...Array(12)].map((_, i) => {
                                const monthVal = i + 1;
                                const monthName = new Date(0, i).toLocaleString('en', { month: 'long' });
                                const monthVouchers = getMonthVouchers(activeLedgerSiblingTab, monthVal, ledgerYear);
                                
                                if (monthVouchers.length === 0) {
                                  return (
                                    <tr key={monthVal} className="text-muted opacity-75">
                                      <td className="fw-semibold">{monthName}</td>
                                      <td colSpan="4" className="text-center py-2 small bg-light-subtle">No vouchers generated for this month</td>
                                    </tr>
                                  );
                                }

                                return monthVouchers.map((fee, idx) => (
                                  <tr key={`${monthVal}-${idx}`}>
                                    {idx === 0 && (
                                      <td rowSpan={monthVouchers.length} className="fw-bold align-middle bg-white text-dark">{monthName}</td>
                                    )}
                                    <td>
                                      <div className="fw-semibold text-dark small">{fee.title}</div>
                                      <small className="text-muted" style={{ fontSize: '10px' }}>ID: #INV-{fee.id} | Due: {new Date(fee.due_date).toLocaleDateString()}</small>
                                    </td>
                                    <td>
                                      <span className="fw-bold text-dark">{getCurrencySymbol(settings?.currency)}{fee.amount}</span>
                                      {fee.late_fine > 0 && <div className="text-danger small" style={{ fontSize: '9px' }}>+ fine: {getCurrencySymbol(settings?.currency)}{fee.late_fine}</div>}
                                    </td>
                                    <td>
                                      <span className={`badge rounded-pill fw-normal px-2 py-1 ${fee.status === 'paid' ? 'bg-success-subtle text-success border-success' : (fee.status === 'overdue' ? 'bg-danger-subtle text-danger border-danger' : 'bg-warning-subtle text-warning border-warning')}`}>
                                        {fee.status.toUpperCase()}
                                      </span>
                                    </td>
                                    <td>
                                      {fee.status === 'paid' ? (
                                        <div className="small text-secondary">
                                          <div>Method: <strong>{fee.payment_method || 'Cash'}</strong></div>
                                          <div style={{ fontSize: '10px' }}>Date: {new Date(fee.paid_date).toLocaleDateString()}</div>
                                        </div>
                                      ) : '-'}
                                    </td>
                                  </tr>
                                ));
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* If nothing is selected / typed yet */}
          {((ledgerType === 'single' && !ledgerStudentId) || (ledgerType === 'family' && !ledgerFamilyCnic)) && (
            <div className="text-center py-5 text-muted border border-dashed rounded-3 bg-light">
              <i className="bi bi-journal-text fs-1 d-block mb-2 text-secondary opacity-50"></i>
              <h5>Select a student or enter CNIC to generate the yearly ledger</h5>
              <p className="small mb-0 text-muted">Complete records of payments, outstanding dues, and monthly invoices.</p>
            </div>
          )}

          <div className="d-flex justify-content-end mt-4 pt-3 border-top">
            <button type="button" className="btn btn-light px-5 rounded-pill" onClick={() => setShowLedgerModal(false)}>Close Ledger</button>
          </div>
        </div>
      </Modal>

      <Modal show={showCategoryModal} onClose={() => setShowCategoryModal(false)} title={editingCategory ? "Edit Fee Category" : "Add Fee Category"}>
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Category Name</label>
          <input type="text" className="form-control" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} placeholder="e.g. Sports Fee" />
        </div>
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Default Amount</label>
          <input type="number" className="form-control" value={categoryForm.amount} onChange={(e) => setCategoryForm({...categoryForm, amount: e.target.value})} />
        </div>
        <div className="mb-4 form-check">
          <input type="checkbox" className="form-check-input" id="isTuition" checked={categoryForm.is_tuition} onChange={(e) => setCategoryForm({...categoryForm, is_tuition: e.target.checked})} />
          <label className="form-check-label small" htmlFor="isTuition">Is this a Tuition Fee? (Amount will automatically pull from the student's assigned course minus discount)</label>
        </div>
        
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-light rounded-pill px-4" onClick={() => setShowCategoryModal(false)}>Cancel</button>
          <button className="btn btn-primary rounded-pill px-4" onClick={handleSaveCategory} disabled={!categoryForm.name}>Save Category</button>
        </div>
        
        <hr className="my-4" />
        <h6 className="fw-bold mb-3">Existing Categories</h6>
        <div className="list-group">
          {feeCategories.map(cat => (
            <div key={cat.id} className="list-group-item d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-bold">{cat.name}</span>
                {cat.is_tuition ? (
                  <span className="badge bg-info ms-2">Tuition</span>
                ) : (
                  <span className="text-muted small ms-2">{getCurrencySymbol(settings?.currency)}{cat.amount}</span>
                )}
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-light text-primary" onClick={() => { setEditingCategory(cat); setCategoryForm(cat); }}><i className="bi bi-pencil"></i></button>
                <button className="btn btn-sm btn-light text-danger" onClick={() => handleDeleteCategory(cat.id)}><i className="bi bi-trash"></i></button>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal show={showCategoryModal} onClose={() => setShowCategoryModal(false)} title={editingCategory ? "Edit Fee Category" : "Add Fee Category"}>
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Category Name</label>
          <input type="text" className="form-control" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} placeholder="e.g. Sports Fee" />
        </div>
        <div className="mb-3">
          <label className="form-label small text-muted fw-bold">Default Amount</label>
          <input type="number" className="form-control" value={categoryForm.amount} onChange={(e) => setCategoryForm({...categoryForm, amount: e.target.value})} />
        </div>
        <div className="mb-4 form-check">
          <input type="checkbox" className="form-check-input" id="isTuition" checked={categoryForm.is_tuition} onChange={(e) => setCategoryForm({...categoryForm, is_tuition: e.target.checked})} />
          <label className="form-check-label small" htmlFor="isTuition">Is this a Tuition Fee? (Amount will automatically pull from the student's assigned course minus discount)</label>
        </div>
        
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-light rounded-pill px-4" onClick={() => setShowCategoryModal(false)}>Cancel</button>
          <button className="btn btn-primary rounded-pill px-4" onClick={handleSaveCategory} disabled={!categoryForm.name}>Save Category</button>
        </div>
        
        <hr className="my-4" />
        <h6 className="fw-bold mb-3">Existing Categories</h6>
        <div className="list-group">
          {feeCategories.map(cat => (
            <div key={cat.id} className="list-group-item d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-bold">{cat.name}</span>
                {cat.is_tuition ? (
                  <span className="badge bg-info ms-2">Tuition</span>
                ) : (
                  <span className="text-muted small ms-2">{getCurrencySymbol(settings?.currency)}{cat.amount}</span>
                )}
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-light text-primary" onClick={() => { setEditingCategory(cat); setCategoryForm(cat); }}><i className="bi bi-pencil"></i></button>
                <button className="btn btn-sm btn-light text-danger" onClick={() => handleDeleteCategory(cat.id)}><i className="bi bi-trash"></i></button>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};
export default FeeList;


